import React, { useState, useRef, useEffect, useCallback } from 'react';
import Styled from 'styled-components';
import { Button, Tooltip, Select } from 'antd';
import {
    LeftOutlined,
    RightOutlined,
    FullscreenOutlined,
    FullscreenExitOutlined,
    DownloadOutlined,
    ExportOutlined,
    BookOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
} from '@ant-design/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Keyboard, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-coverflow';

const { Option } = Select;

const TOTAL_PAGES = 22;
const catalogueUrl = `${process.env.PUBLIC_URL || ''}/documents/unique-products-catalogue.pdf`;
const getPageUrl = (page) => `${process.env.PUBLIC_URL || ''}/documents/catalogue-pages/page-${page}.jpg`;

const CarouselWrapper = Styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
    display: flex;
    flex-direction: column;
    text-align: left;
    user-select: none;
    transition: all 0.2s ease;

    a {
        text-decoration: none !important;
        color: inherit;
    }

    /* Compact Top Toolbar */
    .slider-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 14px;
        background: #ffffff;
        border-bottom: 1px solid #edf2f7;
        gap: 10px;
        flex-wrap: wrap;

        .toolbar-status-group {
            display: flex;
            align-items: center;
            gap: 8px;

            .page-badge {
                background: #fff7ed;
                border: 1px solid #fed7aa;
                color: #ea580c;
                font-size: 11px;
                font-weight: 700;
                padding: 2px 8px;
                border-radius: 4px;
                letter-spacing: 0.2px;
            }

            .auto-indicator {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                font-size: 11px;
                color: #16a34a;
                font-weight: 600;

                .dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #16a34a;
                    animation: pulse 1.5s infinite;
                }
            }

            @keyframes pulse {
                0% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.4; transform: scale(0.8); }
                100% { opacity: 1; transform: scale(1); }
            }
        }

        /* Center Navigation Controls */
        .toolbar-nav-group {
            display: flex;
            align-items: center;
            gap: 6px;

            .nav-btn {
                background: #f8fafc;
                border: 1px solid #cbd5e1;
                color: #1e293b;
                font-size: 11px;
                height: 28px;
                padding: 0 10px;
                border-radius: 6px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover:not(:disabled) {
                    background: #ffffff;
                    border-color: #F58826;
                    color: #ea580c;
                }

                &:disabled {
                    opacity: 0.35;
                    cursor: not-allowed;
                }
            }

            .page-select {
                width: 108px;

                .ant-select-selector {
                    background: #f8fafc !important;
                    border: 1px solid #cbd5e1 !important;
                    color: #0f172a !important;
                    height: 28px !important;
                    border-radius: 6px !important;
                    font-size: 11px !important;
                    display: flex !important;
                    align-items: center !important;
                }

                .ant-select-selection-item {
                    color: #0f172a !important;
                    line-height: 26px !important;
                    font-weight: 600 !important;
                }
            }

            .btn-autoplay-toggle {
                background: #ecfdf5;
                border: 1px solid #a7f3d0;
                color: #047857;
                font-size: 11px;
                font-weight: 600;
                height: 28px;
                padding: 0 10px;
                border-radius: 6px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover {
                    background: #d1fae5;
                }

                &.paused {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                    color: #64748b;
                }
            }
        }

        /* Right Actions */
        .toolbar-actions {
            display: flex;
            align-items: center;
            gap: 6px;

            .action-btn {
                background: #f8fafc;
                border: 1px solid #cbd5e1;
                color: #334155;
                font-size: 11px;
                height: 28px;
                padding: 0 10px;
                border-radius: 6px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                cursor: pointer;
                transition: all 0.15s ease;

                &:hover {
                    background: #ffffff;
                    border-color: #64748b;
                    color: #0f172a;
                }

                &.btn-fullscreen {
                    background: linear-gradient(135deg, #F58826 0%, #ea580c 100%);
                    border-color: #ea580c;
                    color: #ffffff;
                    font-weight: 600;

                    &:hover {
                        background: linear-gradient(135deg, #fb923c 0%, #ea580c 100%);
                    }
                }
            }
        }
    }

    /* Carousel Stage - Main Visual Focus */
    .carousel-stage {
        position: relative;
        width: 100%;
        height: 640px;
        background: linear-gradient(135deg, rgb(9, 26, 50) 0%, rgb(30, 41, 59) 100%);
        border-top: 1px solid rgba(255, 255, 255, 0.08);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        padding: 16px 0;

        @media (max-width: 768px) {
            height: 480px;
            padding: 8px 0;
        }

        /* Swiper Container */
        .swiper {
            width: 100%;
            height: 100%;
            padding: 10px 0;
        }

        .swiper-slide {
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.3s ease, opacity 0.3s ease;
            opacity: 0.55;
            transform: scale(0.9);
            cursor: pointer;

            &.swiper-slide-active {
                opacity: 1;
                transform: scale(1);
                cursor: default;

                .slide-card {
                    box-shadow: 0 20px 45px rgba(23, 90, 160, 0.2), 0 0 0 2px #F58826;
                }
            }

            .slide-card {
                position: relative;
                max-height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 12px;
                overflow: hidden;
                background: #ffffff;
                box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
                transition: box-shadow 0.3s ease;

                img {
                    max-height: calc(640px - 50px);
                    max-width: 90vw;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    display: block;
                    border-radius: 12px;

                    @media (max-width: 768px) {
                        max-height: calc(480px - 40px);
                        max-width: 86vw;
                    }
                }

                .page-badge-overlay {
                    position: absolute;
                    bottom: 12px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: rgba(255, 255, 255, 0.94);
                    backdrop-filter: blur(8px);
                    color: #0f172a;
                    font-size: 11px;
                    font-weight: 700;
                    padding: 3px 14px;
                    border-radius: 999px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    pointer-events: none;
                }
            }
        }

        /* Large Floating Prev/Next Buttons (Frosted White Glass) */
        .stage-nav-arrow {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.92);
            backdrop-filter: blur(8px);
            border: 1px solid #cbd5e1;
            color: #1e293b;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            cursor: pointer;
            z-index: 20;
            transition: all 0.2s ease;
            box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);

            &:hover:not(:disabled) {
                background: #F58826;
                border-color: #F58826;
                color: #ffffff;
                transform: translateY(-50%) scale(1.12);
                box-shadow: 0 10px 28px rgba(245, 136, 38, 0.35);
            }

            &:disabled {
                opacity: 0.25;
                cursor: not-allowed;
            }

            &.arrow-prev {
                left: 20px;
                @media (max-width: 576px) { left: 8px; width: 40px; height: 40px; font-size: 16px; }
            }

            &.arrow-next {
                right: 20px;
                @media (max-width: 576px) { right: 8px; width: 40px; height: 40px; font-size: 16px; }
            }
        }
    }

    /* Fullscreen Mode (Maintains Bright Studio Aesthetics) */
    &.is-fullscreen {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        max-height: 100vh !important;
        margin: 0 !important;
        border-radius: 0 !important;
        border: none !important;
        z-index: 999999 !important;
        background: #ffffff;

        .slider-toolbar {
            padding: 8px 20px;
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .carousel-stage {
            height: calc(100vh - 48px) !important;
            max-height: none !important;
            padding: 14px 0;
            background: linear-gradient(135deg, rgb(9, 26, 50) 0%, rgb(30, 41, 59) 100%);

            .swiper-slide .slide-card img {
                max-height: calc(100vh - 48px - 36px) !important;
            }

            .stage-nav-arrow {
                &.arrow-prev { left: 24px; }
                &.arrow-next { right: 24px; }
            }
        }
    }
`;

export default function CatalogueSlider() {
    const [currentPage, setCurrentPage] = useState(1);
    const [isAutoScroll, setIsAutoScroll] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const swiperRef = useRef(null);
    const containerRef = useRef(null);

    // Auto scroll handling
    const toggleAutoScroll = () => {
        if (!swiperRef.current) return;
        if (isAutoScroll) {
            swiperRef.current.autoplay.stop();
            setIsAutoScroll(false);
        } else {
            swiperRef.current.autoplay.start();
            setIsAutoScroll(true);
        }
    };

    const handleSlideTo = (pageNum) => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
            swiperRef.current.slideToLoop(pageNum - 1);
        }
    };

    const handlePrev = () => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
            swiperRef.current.slidePrev();
        }
    };

    const handleNext = () => {
        if (swiperRef.current && !swiperRef.current.destroyed) {
            swiperRef.current.slideNext();
        }
    };

    // Fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        if (!isFullscreen) {
            setIsFullscreen(true);
            document.body.style.overflow = 'hidden';

            if (containerRef.current && containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen().catch(() => {});
            }
        } else {
            setIsFullscreen(false);
            document.body.style.overflow = '';

            if (document.fullscreenElement && document.exitFullscreen) {
                document.exitFullscreen().catch(() => {});
            }
        }
    }, [isFullscreen]);

    // Keyboard and fullscreen handlers
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setIsFullscreen(false);
                document.body.style.overflow = '';
            }
        };

        const handleKeyDown = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === 'ArrowRight' || e.key === 'PageDown') {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                e.preventDefault();
                handlePrev();
            } else if (e.key === 'Escape' && isFullscreen) {
                toggleFullscreen();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isFullscreen, toggleFullscreen]);

    return (
        <CarouselWrapper
            ref={containerRef}
            className={isFullscreen ? 'is-fullscreen' : ''}
            aria-labelledby="slider-catalogue-title"
        >
            {/* Top Bright Studio Toolbar */}
            <div className="slider-toolbar">
                <div className="toolbar-status-group">
                    <span className="page-badge">
                        Page {currentPage} of {TOTAL_PAGES}
                    </span>
                    {isAutoScroll && (
                        <span className="auto-indicator">
                            <span className="dot" /> Auto-Scroll
                        </span>
                    )}
                </div>

                {/* Center Prev / Page / Next / Auto-Scroll Controls */}
                <div className="toolbar-nav-group">
                    <button
                        className="nav-btn"
                        onClick={handlePrev}
                        title="Previous page (Left arrow key)"
                    >
                        <LeftOutlined />
                    </button>

                    <Select
                        className="page-select"
                        value={currentPage}
                        onChange={(val) => handleSlideTo(val)}
                        dropdownMatchSelectWidth={false}
                    >
                        {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((num) => (
                            <Option key={num} value={num}>
                                Page {num} of {TOTAL_PAGES}
                            </Option>
                        ))}
                    </Select>

                    <button
                        className="nav-btn"
                        onClick={handleNext}
                        title="Next page (Right arrow key)"
                    >
                        <RightOutlined />
                    </button>

                    {/* Auto-Scroll Toggle Button */}
                    <button
                        className={`btn-autoplay-toggle ${!isAutoScroll ? 'paused' : ''}`}
                        onClick={toggleAutoScroll}
                        title={isAutoScroll ? 'Pause auto scrolling' : 'Resume auto scrolling'}
                    >
                        {isAutoScroll ? (
                            <>
                                <PauseCircleOutlined /> Pause
                            </>
                        ) : (
                            <>
                                <PlayCircleOutlined /> Auto
                            </>
                        )}
                    </button>
                </div>

                {/* Right Actions */}
                <div className="toolbar-actions">
                    <Tooltip title="Full Screen">
                        <Button
                            className="action-btn btn-fullscreen"
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                            size="small"
                        >
                            {isFullscreen ? 'Exit' : 'Full'}
                        </Button>
                    </Tooltip>

                    <Tooltip title="Open original PDF in a new browser tab">
                        <a href={catalogueUrl} target="_blank" rel="noopener noreferrer">
                            <Button className="action-btn" icon={<ExportOutlined />} size="small" />
                        </a>
                    </Tooltip>

                    <Tooltip title="Download original PDF file (43 MB)">
                        <a href={catalogueUrl} download>
                            <Button className="action-btn" icon={<DownloadOutlined />} size="small" />
                        </a>
                    </Tooltip>
                </div>
            </div>

            {/* Studio Light Backdrop Stage with Multi-Slide Peeking & Floating Arrows */}
            <div className="carousel-stage">
                <button
                    className="stage-nav-arrow arrow-prev"
                    onClick={handlePrev}
                    aria-label="Previous Page"
                    title="Previous page"
                >
                    <LeftOutlined />
                </button>

                <Swiper
                    modules={[Navigation, Autoplay, Keyboard, EffectCoverflow]}
                    onSwiper={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    onSlideChange={(swiper) => {
                        setCurrentPage(swiper.realIndex + 1);
                    }}
                    centeredSlides={true}
                    slidesPerView={1.6}
                    spaceBetween={24}
                    loop={true}
                    loopAdditionalSlides={2}
                    loopPreventsSliding={false}
                    autoplay={{
                        delay: 3500,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                    }}
                    keyboard={{ enabled: true }}
                    breakpoints={{
                        320: { slidesPerView: 1.1, spaceBetween: 12 },
                        640: { slidesPerView: 1.3, spaceBetween: 18 },
                        1024: { slidesPerView: 1.6, spaceBetween: 24 },
                        1440: { slidesPerView: 2.1, spaceBetween: 30 },
                    }}
                >
                    {Array.from({ length: TOTAL_PAGES }, (_, i) => i + 1).map((num) => (
                        <SwiperSlide key={num} onClick={() => handleSlideTo(num)}>
                            <div className="slide-card">
                                <img
                                    src={getPageUrl(num)}
                                    alt={`Catalogue Page ${num}`}
                                    loading="lazy"
                                />
                                <div className="page-badge-overlay">Page {num}</div>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>

                <button
                    className="stage-nav-arrow arrow-next"
                    onClick={handleNext}
                    aria-label="Next Page"
                    title="Next page"
                >
                    <RightOutlined />
                </button>
            </div>
        </CarouselWrapper>
    );
}
